import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { BUILD, SOCKET_URL } from '../config';

const useStyles = makeStyles()((theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    minWidth: 320,
  },
  // label / value rows.
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
  label: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  value: {
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  appName: {
    fontWeight: 700,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
  },
}));

// Localize the ISO build timestamp; tolerate a null builtAt (dev / unknown).
const formatBuiltAt = (iso) => {
  if (!iso) return 'dev build';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'dev build' : d.toLocaleString();
};

/**
 * About dialog — user-facing version info. Deliberately shows only the semver
 * version + build date (NO git SHA — a raw commit hash isn't standard user UI;
 * operators read the SHA from the boot console banner / /api/health instead).
 * On open it fetches /api/health once to display the live backend version.
 */
const AboutDialog = ({ open, onClose }) => {
  const { classes } = useStyles();
  const [backend, setBackend] = useState(null); // { version, status } | { error }

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setBackend(null);
    fetch(`${SOCKET_URL}/api/health`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!cancelled) setBackend({ version: data.version, status: data.status });
      })
      .catch(() => {
        if (!cancelled) setBackend({ error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <DialogTitle>About ChatCove</DialogTitle>
      <DialogContent>
        <Typography className={classes.appName}>ChatCove</Typography>

        <Typography className={classes.sectionLabel}>App</Typography>
        <div className={classes.row}>
          <span className={classes.label}>Version</span>
          <span className={classes.value}>{BUILD.version}</span>
        </div>
        <div className={classes.row}>
          <span className={classes.label}>Built</span>
          <span className={classes.value}>{formatBuiltAt(BUILD.builtAt)}</span>
        </div>

        <Typography className={classes.sectionLabel}>Server</Typography>
        <div className={classes.row}>
          <span className={classes.label}>Version</span>
          <span className={classes.value}>
            {backend === null
              ? '…'
              : backend.error
              ? 'unavailable'
              : backend.version || 'unknown'}
          </span>
        </div>
        <div className={classes.row}>
          <span className={classes.label}>Status</span>
          <span className={classes.value}>
            {backend === null
              ? '…'
              : backend.error
              ? 'offline'
              : backend.status || 'OK'}
          </span>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AboutDialog;
